import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { clearUser } from '../store/userSlice';
import { clearLastAllowedPath } from './ModuleAccessGuard';
import { handlePermissionsSocketMessage } from '../utils/permissionSync';
import { toast } from 'react-toastify';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL
const WS_URL = process.env.REACT_APP_WEBSOCKET_URL
const WS_URL_PLACEHOLDER = 'your-websocket-endpoint'
const WS_RECONNECT_DELAY_MS = 5000

function isWebSocketConfigured() {
  return Boolean(WS_URL && !WS_URL.includes(WS_URL_PLACEHOLDER))
}

const Header = () => {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [showNotifications, setShowNotifications] = useState(false)
  const [notificationPermission, setNotificationPermission] = useState('default')
  const [showAllNotifications, setShowAllNotifications] = useState(false); 
  const wsRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const shouldReconnectRef = useRef(true)
  const MAX_VISIBLE = 5;
  const notificationRef = useRef(null);
  
  const getFirstLetter = (name) => {
    if (!name) return 'U';
    return name.charAt(0).toUpperCase();
  };
  
  const capitalizeName = (name) => {
    if (!name) return 'User';
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  const userName = capitalizeName(user?.name);
  const userCharacter = getFirstLetter(user?.name);

  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission)
      
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          setNotificationPermission(permission)
          if (permission === 'granted') {
            console.log('Desktop notifications enabled')
          } else {
            console.log('Desktop notifications denied')
          }
        })
      }
    } else {
      console.log('Browser does not support notifications')
    }
  }, [])

  const showDesktopNotification = useCallback((notification) => {
    if (!('Notification' in window)) {
      console.log('This browser does not support desktop notifications')
      return
    }

    if (Notification.permission === 'granted') {
      const notificationOptions = {
        body: notification.message,
        icon: '/assets/img/color icon_circle.png',
        badge: '/assets/img/color icon_circle.png',
        tag: notification._id || 'notification-' + Date.now(),
        requireInteraction: false,
        data: {
          notificationId: notification._id,
          taskId: notification.data?.taskId,
          rfqId:notification.data?.rfqId,
          type: notification.type
        },
        vibrate: [200, 100, 200] // Vibration pattern for mobile
      }

      const desktopNotification = new Notification(notification.title, notificationOptions)

     desktopNotification.onclick = (event) => {
  event.preventDefault()
  window.focus() // Focus the browser window

        if (notification.data?.taskId) {
            navigate(`/taskView/${notification.data.taskId}`);
          } else if (notification.data?.rfqId) {
            navigate(`/rfqView/${notification.data.rfqId}`);
          }

  markAsRead(notification._id)
  desktopNotification.close()

}

      desktopNotification.onclose = () => {
        console.log('Desktop notification closed')
      }

      desktopNotification.onerror = (error) => {
        console.error('Desktop notification error:', error)
      }

      setTimeout(() => {
        desktopNotification.close()
      }, 10000)
    } else if (Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        setNotificationPermission(permission)
        if (permission === 'granted') {
          showDesktopNotification(notification)
        }
      })
    }
  }, [navigate])

  const fetchNotifications = useCallback(async () => {
    const userId = localStorage.getItem('id')
    if (!userId) return

    try {
      const payload = {
        action: 'get-notifications',
        userId: userId,
      }
      const response = await axios.post(API_URL, payload)
      
      let responseData = response.data
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch (e) {
          responseData = response.data
        }
      }

      if (responseData.success && responseData.data) {
        setNotifications(responseData.data.notifications || [])
        setUnreadCount(responseData.data.unreadCount || 0)
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
    }
  }, [])

  useEffect(() => {
  const handleClickOutside = (event) => {
    if (
      notificationRef.current &&
      !notificationRef.current.contains(event.target)
    ) {
      setShowNotifications(false)
    }
  }

  document.addEventListener('mousedown', handleClickOutside)

  return () => {
    document.removeEventListener('mousedown', handleClickOutside)
  }
}, [])


  const markAllAsRead = async () => {
  const userId = localStorage.getItem('id');
  if (!userId) return;

  try {
    setNotifications(prev => prev.map(notif => ({ ...notif, isRead: true })));
    setUnreadCount(0); 
    await axios.post(API_URL, {
      action: 'mark-allnotification-read',
      userId
    });

    
    console.log('All notifications marked as read');

  } catch (error) {
    console.error('Failed to mark all as read:', error);
  }
};

  const showBannerNotification = useCallback((notification) => {
    showDesktopNotification(notification)
  }, [showDesktopNotification])

  const testBannerNotification = useCallback(() => {
    const testNotification = {
      _id: 'test-' + Date.now(),
      title: '🔔 New Task Assigned',
      message: 'You have been assigned a new task. Click to view details.',
      type: 'task-assigned',
      isRead: false,
      createdAt: new Date().toISOString(),
      data: {
        taskDescription: 'Complete the project documentation',
        taskId: 'test-task-123'
      }
    }
    showBannerNotification(testNotification)
  }, [showBannerNotification])

  // Expose test functions to window for debugging
  useEffect(() => {
    window.testBanner = testBannerNotification
    window.testDesktopNotification = () => {
      const testNotif = {
        _id: 'test-desktop-' + Date.now(),
        title: '🔔 New Task Assigned',
        message: 'You have been assigned a new task. Click to view details.',
        type: 'task-assigned',
        data: {
          taskDescription: 'Complete the project documentation',
          taskId: 'test-task-123'
        }
      }
      showDesktopNotification(testNotif)
    }
    console.log('Test functions available:')
    console.log('- window.testBanner() - Test in-app banner + desktop notification')
    console.log('- window.testDesktopNotification() - Test desktop notification only')
  }, [testBannerNotification, showDesktopNotification])

  const handleSocketPayload = useCallback(async (data) => {
    if (!data || typeof data !== 'object') return

    const permissionsUpdated = await handlePermissionsSocketMessage(dispatch, data)
    if (permissionsUpdated) {
      toast.info('Your permissions were updated.', { autoClose: 3000 })
      return
    }

    const inlineNotification =
      data.notification ||
      (data.type === 'notification' ? data.payload : null) ||
      (data.action === 'notification' ? data.data : null)

    if (inlineNotification?.title || inlineNotification?.message) {
      showBannerNotification(inlineNotification)
    }

    const shouldSyncList =
      data.type === 'notification' ||
      data.action === 'notification' ||
      data.event === 'notification' ||
      data.refreshNotifications === true ||
      Boolean(inlineNotification)

    if (shouldSyncList) {
      fetchNotifications()
    }
  }, [dispatch, showBannerNotification, fetchNotifications])

  const connectWebSocket = useCallback(() => {
    const userId = localStorage.getItem('id')
    const userType = localStorage.getItem('role') || 'user'

    if (!userId) return

    if (!isWebSocketConfigured()) {
      console.warn(
        'REACT_APP_WEBSOCKET_URL is not configured. Real-time notifications are disabled. ' +
          'Update .env with your WebSocket endpoint (no continuous API polling will run).'
      )
      return
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    if (wsRef.current) {
      wsRef.current.onclose = null
      wsRef.current.close()
      wsRef.current = null
    }

    try {
      const ws = new WebSocket(`${WS_URL}?userId=${userId}&userType=${userType}`)

      ws.onopen = () => {
        console.log('WebSocket connected')
        ws.send(
          JSON.stringify({
            action: 'ping',
            userId,
            userType
          })
        )
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          console.log('WebSocket message received:', data)
          handleSocketPayload(data)
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
      }

      ws.onclose = () => {
        console.log('WebSocket disconnected, reconnecting...')
        wsRef.current = null
        if (!shouldReconnectRef.current || !isWebSocketConfigured()) return
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current)
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket()
        }, WS_RECONNECT_DELAY_MS)
      }

      wsRef.current = ws
    } catch (error) {
      console.error('Failed to connect WebSocket:', error)
      if (!shouldReconnectRef.current || !isWebSocketConfigured()) return
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      reconnectTimeoutRef.current = setTimeout(() => {
        connectWebSocket()
      }, WS_RECONNECT_DELAY_MS)
    }
  }, [handleSocketPayload])

  useEffect(() => {
    const userId = localStorage.getItem('id')
    if (!userId) return

    shouldReconnectRef.current = true
    // Defer so page APIs (RFQ count/list/users/client-ref) finish first and avoid API contention.
    const notificationsTimer = setTimeout(() => {
      fetchNotifications()
    }, 10000)
    connectWebSocket()

    return () => {
      clearTimeout(notificationsTimer)
      shouldReconnectRef.current = false
      if (wsRef.current) {
        wsRef.current.onclose = null
        wsRef.current.close()
        wsRef.current = null
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }
  }, [connectWebSocket, fetchNotifications])

  const formatNotificationTime = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }
const handleNotificationClick = (notification) => {
    setNotifications((prev) =>
      prev.map((notif) =>
        notif._id === notification._id ? { ...notif, isRead: true } : notif,
      ),
    );

    setUnreadCount((prev) => Math.max(0, prev - 1));

    markAsRead(notification._id);

     if (notification.data?.taskId) {
     navigate(`/taskView/${notification.data.taskId}`);
     } else if (notification.data?.rfqId) {
       navigate(`/rfqView/${notification.data.rfqId}`);
     }
     setShowNotifications(false);
  };

  const markAsRead = async (notificationId) => {
    try {
      const userId = localStorage.getItem("id");
      if (!userId) return;

      await axios.post(API_URL, {
        action: "mark-notification-read",
        notificationId,
        userId,
      });
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleLogout = (e) => {
    e.preventDefault();

    shouldReconnectRef.current = false
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.onclose = null
      wsRef.current.close()
      wsRef.current = null
    }
    
    localStorage.clear();
    clearLastAllowedPath();

    dispatch(clearUser());
    
    navigate('/');
  };

  return (
    <div>
      <nav className="main-header navbar navbar-expand navbar-white navbar-light">
        {/* Left navbar links */}
        <ul className="navbar-nav">
          <li className="nav-item">
            <a
              className="nav-link"
              data-widget="pushmenu"
              href="#header-pushmenu"
              role="button"
              onClick={(e) => e.preventDefault()}
            >
              <i className="fas fa-bars" />
            </a>
          </li>
        </ul>
        <ul className="navbar-nav ml-auto">
          <li className="nav-item dropdown" ref={notificationRef}>
            <a 
              className="nav-link" 
              data-toggle="dropdown" 
              href="#header-notifications"
              onClick={(e) => {
                e.preventDefault()
                const willOpen = !showNotifications
                setShowNotifications(willOpen)
                if (willOpen) {
                  fetchNotifications()
                }
              }}
            >
              <i className="far fa-bell" style={{ fontSize: '20px' }} />
              {unreadCount > 0 && (
                <span className="badge badge-danger navbar-badge" style={{ fontSize: '10px' }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </a>
            <div className={`dropdown-menu dropdown-menu-lg dropdown-menu-right ${showNotifications ? 'show' : ''}`} style={{ width: '350px', maxHeight: '400px', overflowY: 'auto' }}>
              <div className="dropdown-item dropdown-header d-flex justify-content-between align-items-center">
                <span>{unreadCount} Unread Notification{unreadCount !== 1 ? 's' : ''}</span>
                {unreadCount > 0 && (
                 <button 
                   className="btn btn-sm btn-link p-0" 
                   onClick={(e) => {
                   e.preventDefault();
                   e.stopPropagation();   
                   markAllAsRead();
                    }}
                    style={{ fontSize: '12px' }}>
                      Mark all read
                   </button>
                )}
              </div>
              
              {/* Desktop Notification Permission Status */}
              {notificationPermission !== 'granted' && (
                <div className="dropdown-item" style={{ backgroundColor: '#fff3cd', borderLeft: '3px solid #ffc107' }}>
                  <small className="d-flex align-items-center justify-content-between">
                    <span>
                      <i className="fas fa-exclamation-triangle mr-1" style={{ color: '#856404' }} />
                      Desktop notifications disabled
                    </span>
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => {
                        Notification.requestPermission().then(permission => {
                          setNotificationPermission(permission)
                          if (permission === 'granted') {
                            alert('Desktop notifications enabled! You will now receive notifications even when the browser is minimized.')
                          }
                        })
                      }}
                      style={{ fontSize: '11px', padding: '2px 8px' }}
                    >
                      Enable
                    </button>
                  </small>
                </div>
              )}
              
              <div className="dropdown-divider" />
              
      
              {notifications.length === 0 ? (
                <div className="dropdown-item text-center text-muted py-3">
                  No notifications
                </div>
              ) : (
                <>
                  {(showAllNotifications
                    ? notifications
                    : notifications.slice(0, MAX_VISIBLE)
                  ).map((notification) => (
                    <div key={notification._id}>
                      <a
                        href={`#notification-${notification._id}`}
                        className={`dropdown-item ${!notification.isRead ? "bg-light" : ""}`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNotificationClick(notification);
                        }}
                        style={{ whiteSpace: "normal", cursor: "pointer" }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div style={{ flex: 1 }}>
                            <span style={{ fontSize: "14px",color:"#3498db",fontWeight: "500"}}>
                              {notification.title}
                            </span>
                            <p
                              className="mb-1"
                              style={{ fontSize: "12px", color: "#666" }}
                            >
                              {notification.message}
                            </p>
                            <small className="text-muted">
                              <i className="far fa-clock mr-1" />
                              {formatNotificationTime(notification.createdAt)}
                            </small>
                          </div>
                          {!notification.isRead && (
                            <span
                              className="badge badge-primary ml-2"
                              style={{ fontSize: "8px" }}
                            >
                              NEW
                            </span>
                          )}
                        </div>
                      </a>
                      <div className="dropdown-divider" />
                    </div>
                  ))}
                  {!showAllNotifications &&
                    notifications.length > MAX_VISIBLE && (
                      <div className="dropdown-item text-center text-muted py-1">
                        +{notifications.length - MAX_VISIBLE} more notifications
                      </div>
                    )}

                  {notifications.length > MAX_VISIBLE && (
                    <a
                      href="#header-notifications-view-all"
                      className="dropdown-item dropdown-footer text-center"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowAllNotifications(!showAllNotifications);
                      }}
                    >
                      {showAllNotifications
                        ? "Show Less"
                        : "View All Notifications"}
                    </a>
                  )}
                </>
              )}
            </div>
          </li>

          {/* User Profile Dropdown */}
          <li className="nav-item dropdown">
            <a className="nav-link p-1" data-toggle="dropdown" href="#header-user-menu">
              <div className="user-panel d-flex">
                <div className="image user_div">
                  {/* <img src="dist/img/user.png" class="img-circle elevation-2" alt="User Image"> */}
                  <span className="usercharacter">{userCharacter}</span>
                </div>
                <div className="info">
                  <span className="d-block f_black">{userName} <i className="fa-solid fa-angle-down" /></span>
                </div>
              </div>
            </a>
            <div className="dropdown-menu dropdown-menu-right">
              <Link to ="/profile" className="dropdown-item mb-2">
                <i className="fa-solid fa-user mr-2" /> Profile
              </Link>
              <div className="dropdown-item crsr_pntr" onClick={handleLogout}>
                <i className="fa-solid fa-arrow-right-from-bracket mr-2" /> Logout
              </div>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  )
}

export default Header